import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Input,
  HStack,
  IconButton,
  Text,
  Button,
  Flex,
  Image,
} from '@chakra-ui/react';
import { CloseIcon } from '@chakra-ui/icons';
import { pxToRem } from '../../utils/editor';
import { Editor } from '@tiptap/core';
import useOutlineStore from '../../stores/modules/outline';

interface FindDialogProps {
  isOpen: boolean;
  onClose: () => void;
  editor: Editor;
}

const FindDialog: React.FC<FindDialogProps> = ({ isOpen, onClose, editor }) => {
  const [searchText, setSearchText] = useState('');
  const [replaceText, setReplaceText] = useState('');
  const [currentMatch, setCurrentMatch] = useState(0);
  const [totalMatches, setTotalMatches] = useState(0);
  const [showReplace, setShowReplace] = useState(false);
  const { setOutlineIsChange } = useOutlineStore();

  // 搜索功能实现
  // 修复handleSearch函数
  const handleSearch = useCallback((text: string) => {
    if (!text || !editor) {
      // 清空搜索
      if (editor?.commands?.unsetSearchTerm) {
        editor.commands.unsetSearchTerm();
      }
      setTotalMatches(0);
      setCurrentMatch(0);
      return;
    }
    
    if (editor?.commands?.setSearchTerm) {
      editor.commands.setSearchTerm(text);
      
      // 获取搜索结果统计 - 修复状态获取方式
      setTimeout(() => {
        const searchResults = editor.storage?.searchAndReplace?.results || [];
        const currentIndex = editor.storage?.searchAndReplace?.resultIndex || 0;
        
        setTotalMatches(searchResults.length);
        setCurrentMatch(searchResults.length > 0 ? ( currentIndex>= totalMatches ? 1 : currentIndex + 1) : 0);
      }, 100); // 增加延迟时间确保搜索完成
    }
  }, [editor]);
  

  const handleNext = useCallback(() => {
    if (!editor || totalMatches === 0) return;

    if (editor?.commands?.nextSearchResult) {
      editor.commands.nextSearchResult();
    } 
    
    setCurrentMatch(prev => {
      const nextMatch = prev >= totalMatches ? 1 : prev + 1;
      console.log('Next - currentMatch:', prev, 'nextMatch:', nextMatch, 'totalMatches:', totalMatches);
      return nextMatch;
    });
  }, [editor, totalMatches]);
  
  const handlePrevious = useCallback(() => {
    if (!editor || totalMatches === 0) return;

    if (editor?.commands?.previousSearchResult) {
      editor.commands.previousSearchResult();
    }
    
    setCurrentMatch(prev => {
      const prevMatch = prev <= 1 ? totalMatches : prev - 1;
      console.log('Previous - currentMatch:', prev, 'prevMatch:', prevMatch, 'totalMatches:', totalMatches);
      return prevMatch;
    });
  }, [editor, totalMatches]);
  
 // 替换功能
  const handleReplace = useCallback(() => {
    if (!editor || !replaceText || totalMatches === 0) return;

    // 获取当前搜索结果和索引
    const searchResults = editor.storage?.searchAndReplace?.results || [];
    const replaceIndex = editor.storage?.searchAndReplace?.resultIndex || 0;
    if (searchResults.length === 0 || replaceIndex  >= searchResults.length) return;
    
    // 获取当前选中匹配项的位置
    const currentResult = searchResults[replaceIndex];
    const { from, to } = currentResult;
  
    // 执行替换操作
    editor.chain()
      .focus()
      .setTextSelection({ from, to })
      .insertContent(replaceText)
      .run();
    
    // 检查是否已经替换到最后一个匹配项，如果是则重置索引到0
    if (replaceIndex === searchResults.length - 1) {
      // 重置索引到第一个匹配项
      if (editor.storage?.searchAndReplace) {
        editor.storage.searchAndReplace.resultIndex = 0;
      }
    }
    
  // 触发保存编辑器内容 - 通过焦点切换
  editor.commands.focus(); // 给编辑器焦点
  
  setTimeout(() => {
    editor.commands.blur(); // 失去焦点，触发onBlur事件
    setOutlineIsChange(true); // 额外保险
  }, 50);
    
    // 重新搜索以更新结果
    setTimeout(() => {
      handleSearch(searchText);
    }, 100);
  }, [editor, replaceText, totalMatches, searchText, handleSearch, setOutlineIsChange]);
  
  // 替换全部功能
  const handleReplaceAll = useCallback(() => {
    if (!editor || !replaceText || !searchText) return;
   
    if (editor?.commands?.setReplaceTerm) {
      editor.commands.setReplaceTerm(replaceText);
    }
    
    // 然后执行全部替换
    if (editor?.commands?.replaceAll) {
      editor.commands.replaceAll();
    }
    
   // 触发保存编辑器内容 - 通过焦点切换
  editor.commands.focus(); // 给编辑器焦点
  
  setTimeout(() => {
    editor.commands.blur(); // 失去焦点，触发onBlur事件
    setOutlineIsChange(true); // 额外保险
  }, 50);
    
    // 清空搜索结果
    setTimeout(() => {
      setTotalMatches(0);
      setCurrentMatch(0);
  
      if (editor?.commands?.unsetSearchTerm) {
        editor.commands.unsetSearchTerm();
      }
    }, 100);
  }, [editor, replaceText, searchText, setOutlineIsChange]);

  // 监听搜索文本变化
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      handleSearch(searchText);
    }, 300); // 防抖处理

    return () => clearTimeout(timeoutId);
  }, [searchText, handleSearch]);

  // 组件关闭时清理搜索状态
 useEffect(() => {
     if (!isOpen && editor) {
      // 清空编辑器中的搜索高亮
      if (editor?.commands?.setSearchTerm) {
        editor.commands.setSearchTerm('');
      }
      // 清空组件状态
      setSearchText('');
      setReplaceText('');
      setCurrentMatch(0);
      setTotalMatches(0);
      setShowReplace(false);
    }
  }, [isOpen, editor]);

  if (!isOpen) return null;

  return (
    <>
      {/* Find Dialog */}

        <Box
          position="fixed"
          top="20px"
          left={pxToRem(700)}
          bg="white"
          borderRadius="8px"
          boxShadow="0 4px 12px rgba(0, 0, 0, 0.15)"
          border="1px solid #E2E8F0"
          pl={'30px'}
          pr={'60px'}
          py={'20px'}
          pb={'20px'}
          w="420px"
          zIndex={1000}
        >
          {/* Header */}
          <Flex justify="space-between" align="center">
            <Text fontSize="20px" fontWeight="700" color="#333">
              Find
            </Text>
            <IconButton
              aria-label="Close"
              icon={<CloseIcon />}
              size="xs"
              variant="ghost"
              onClick={onClose}
              transform={'translateX(30px)'}
            />
          </Flex>

          {/* Search Input */}
          <HStack mb={'12px'}>
            <Box w='140px' m={0} fontSize="16px" fontWeight="600" color="#333" >
              Find
            </Box>
            <Input
              placeholder="Find"
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              fontSize="14px"
              height="32px"
              borderRadius="4px"
              border="1px solid #D1D5DB"
              _focus={{
                borderColor: '#3B82F6',
                boxShadow: '0 0 0 1px #3B82F6'
              }}
              autoFocus
            />

          </HStack>
          
            {
              showReplace && (
                <><Box>
                    <HStack mb={'12px'}>
              <Box w='140px' fontSize="16px" fontWeight="600" color="#333" mb={0}>
                Replace with
              </Box>
              <Input
                placeholder="Replace with"
                value={replaceText}
                onChange={(e) => setReplaceText(e.target.value)}
                fontSize="14px"
                height="32px"
                borderRadius="4px"
                border="1px solid #D1D5DB"
                _focus={{
                  borderColor: '#3B82F6',
                  boxShadow: '0 0 0 1px #3B82F6'
                }}
              />
              </HStack>
            </Box></>)
              }
 
          {/* Match Count and Replace Button */}
          <HStack justify="space-between" mt="20px">
         <HStack>
           <Image cursor={'pointer'} src={'/static/editor/left-icon.svg'}  onClick={handlePrevious}   w="24px" h="24px" marginRight={'10px'} />
          <Image cursor={'pointer'} src={'/static/editor/right-icon.svg'}  onClick={handleNext} w="24px" h="24px" marginRight={'4px'} />
            <Text m={0} fontSize="16px" color="#000">
              {totalMatches > 0 ? `${currentMatch}/${totalMatches}` : '0/0'}
            </Text>
         </HStack>
        

            {
              showReplace ? (
                <>
                 <HStack spacing={'12px'}>
                   <Button
                  size="sm"
             variant="outline"
                  onClick={handleReplaceAll}
                  fontSize="12px"
                  height="28px"
                  
                      fontWeight={'bold'}
                  isDisabled={totalMatches === 0 || !replaceText}
                   borderColor="#1D8AFF"
                  color="#1D8AFF"
                  _hover={{ background: '#1D8AFF', color: '#fff' }}
                >
                  Replace all
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleReplace}
                  fontSize="12px"
                  height="28px"
                  fontWeight={'bold'}
                  isDisabled={totalMatches === 0 || !replaceText}
                  borderColor="#1D8AFF"
                  color="#1D8AFF"
                  _hover={{ background: '#1D8AFF', color: '#fff' }}
                >
                  Replace
                </Button>
               
              </HStack>
                </>
              ) : (    <Button
              variant="link"
              size="sm"
              onClick={() => setShowReplace(true)}
              fontSize="12px"
              color="#3B82F6"
              p={0}
              h="auto"
            >
              Replace
            </Button>)
            }
          </HStack>
        </Box>

    </>
  );
};

export default FindDialog;